import './Skeleton.css'

// Base Skeleton component with customizable dimensions
export const Skeleton = ({ 
  width = '100%', 
  height = '1rem', 
  borderRadius = '4px',
  style = {},
  className = ''
}) => (
  <div 
    className={`skeleton ${className}`}
    style={{ 
      width, 
      height, 
      borderRadius,
      ...style 
    }}
  />
)

// Text line skeleton
export const SkeletonText = ({ 
  lines = 1, 
  gap = '0.5rem',
  lastLineWidth = '60%',
  className = ''
}) => (
  <div className={`skeleton-text ${className}`} style={{ gap }}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton 
        key={i} 
        width={i === lines - 1 && lines > 1 ? lastLineWidth : '100%'}
        height="0.875rem"
      />
    ))}
  </div>
)

// Avatar/Circle skeleton
export const SkeletonAvatar = ({ 
  size = '48px',
  className = ''
}) => (
  <Skeleton 
    width={size} 
    height={size} 
    borderRadius="50%" 
    className={`skeleton-avatar ${className}`}
  />
)

// Button skeleton
export const SkeletonButton = ({ 
  width = '100px', 
  height = '36px',
  className = ''
}) => (
  <Skeleton 
    width={width} 
    height={height} 
    borderRadius="6px"
    className={`skeleton-button ${className}`}
  />
)

// Card skeleton
export const SkeletonCard = ({ 
  height = '120px',
  className = ''
}) => (
  <div className={`skeleton-card ${className}`} style={{ height }}>
    <Skeleton height="100%" borderRadius="8px" />
  </div>
)

// Server Status Skeleton
export const ServerStatusSkeleton = () => (
  <div className="skeleton-server-status">
    <div className="skeleton-server-status__row">
      <Skeleton width="18px" height="18px" borderRadius="50%" />
      <Skeleton width="60px" height="16px" />
    </div>
  </div>
)

// Character Card Skeleton
export const CharacterCardSkeleton = () => (
  <div className="skeleton-character-card">
    <div className="skeleton-character-card__avatar">
      <SkeletonAvatar size="48px" />
    </div>
    <div className="skeleton-character-card__info">
      <Skeleton width="120px" height="16px" />
      <Skeleton width="80px" height="12px" style={{ marginTop: '6px' }} />
    </div>
    <div className="skeleton-character-card__level">
      <Skeleton width="50px" height="20px" borderRadius="10px" />
    </div>
  </div>
)

// Character List Skeleton (multiple cards)
export const CharacterListSkeleton = ({ count = 3 }) => (
  <div className="skeleton-character-list">
    {Array.from({ length: count }).map((_, i) => (
      <CharacterCardSkeleton key={i} />
    ))}
  </div>
)

// Account Info Skeleton
export const AccountInfoSkeleton = () => (
  <div className="skeleton-account-info">
    <div className="skeleton-account-info__row">
      <Skeleton width="18px" height="18px" borderRadius="4px" />
      <Skeleton width="80px" height="14px" />
      <Skeleton width="120px" height="14px" style={{ marginLeft: 'auto' }} />
    </div>
    <div className="skeleton-account-info__row">
      <Skeleton width="18px" height="18px" borderRadius="4px" />
      <Skeleton width="100px" height="14px" />
      <Skeleton width="80px" height="14px" style={{ marginLeft: 'auto' }} />
    </div>
    <div className="skeleton-account-info__row">
      <Skeleton width="18px" height="18px" borderRadius="4px" />
      <Skeleton width="90px" height="14px" />
      <Skeleton width="150px" height="14px" style={{ marginLeft: 'auto' }} />
    </div>
    <div className="skeleton-account-info__row">
      <Skeleton width="18px" height="18px" borderRadius="4px" />
      <Skeleton width="70px" height="14px" />
      <Skeleton width="100px" height="14px" style={{ marginLeft: 'auto' }} />
    </div>
  </div>
)

// Character Modal Header Skeleton
export const CharacterHeaderSkeleton = () => (
  <div className="skeleton-char-header">
    <div className="skeleton-char-header__avatar">
      <SkeletonAvatar size="80px" />
      <Skeleton width="80px" height="24px" borderRadius="12px" style={{ marginTop: '8px' }} />
    </div>
    <div className="skeleton-char-header__info">
      <Skeleton width="180px" height="24px" />
      <Skeleton width="100px" height="14px" style={{ marginTop: '8px' }} />
      <Skeleton width="60px" height="14px" style={{ marginTop: '6px' }} />
    </div>
  </div>
)

// Stats Grid Skeleton
export const StatsGridSkeleton = ({ count = 8 }) => (
  <div className="skeleton-stats-grid">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="skeleton-stat-card">
        <Skeleton width="24px" height="24px" borderRadius="4px" />
        <div className="skeleton-stat-card__info">
          <Skeleton width="60px" height="12px" />
          <Skeleton width="40px" height="16px" style={{ marginTop: '4px' }} />
        </div>
      </div>
    ))}
  </div>
)

// Admin Panel Client Row Skeleton
export const ClientRowSkeleton = () => (
  <div className="skeleton-client-row">
    <Skeleton width="40px" height="16px" />
    <Skeleton width="120px" height="16px" />
    <Skeleton width="180px" height="16px" />
    <Skeleton width="80px" height="24px" borderRadius="12px" />
    <Skeleton width="100px" height="16px" />
  </div>
)

// Admin Panel Clients List Skeleton
export const ClientsListSkeleton = ({ count = 5 }) => (
  <div className="skeleton-clients-list">
    {Array.from({ length: count }).map((_, i) => (
      <ClientRowSkeleton key={i} />
    ))}
  </div>
)

// Image loading skeleton (for character avatars)
export const ImageSkeleton = ({ 
  width = '100%', 
  height = '100%',
  className = ''
}) => (
  <div className={`skeleton-image ${className}`}>
    <Skeleton width={width} height={height} borderRadius="8px" />
  </div>
)

// GM List Skeleton (for server status)
export const GMListSkeleton = ({ count = 2 }) => (
  <div className="skeleton-gm-list">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="skeleton-gm-item">
        <Skeleton width="100px" height="14px" />
        <Skeleton width="80px" height="12px" />
        <Skeleton width="50px" height="12px" />
      </div>
    ))}
  </div>
)

export default Skeleton
